import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Upload,
  Brain,
  FileText,
  Target,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Layout,
  ScrollText as Receipt,
  Coins,
  Eye,
  BarChart3,
  Landmark,
  Settings as SettingsIcon,
  Crown,
} from 'lucide-react';
import '../styles/guide.css';

interface Section {
  id: string;
  label: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  example: string;
  tips: string[];
  cta: string;
  route: string;
  badge?: string;
}

const SECTIONS: Section[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <BarChart3 className="w-5 h-5" />,
    title: 'Dashboard',
    description: 'See everything at a glance — document counts, status, money in and out, and what is overdue.',
    example: 'Monday morning: open the dashboard. See 12 documents processed, €42k paid this month, 3 overdue. Monthly chart, top vendors and buyers, and filters to drill in.',
    tips: [
      'Counts by status: analysed, created, ready, confirmed, overdue',
      'Monthly activity chart + top vendors/buyers this month',
      'Search and filter, then export the result to CSV',
    ],
    cta: 'Go to Dashboard',
    route: '/dashboard',
  },
  {
    id: 'upload',
    label: 'Upload',
    icon: <Upload className="w-5 h-5" />,
    title: 'Upload & Extract',
    description: 'Drop a PDF invoice and get all key fields extracted in seconds — no manual entry.',
    example: 'You receive an invoice from Digital AIM. Drop the PDF in. In seconds: Invoice #INV-2025-042, total €1,234, seller, buyer, due date — all extracted automatically.',
    tips: [
      'Supports any PDF layout',
      '26 invoice fields extracted automatically',
      'AI validates low-confidence fields',
    ],
    cta: 'Go to Dashboard',
    route: '/dashboard',
  },
  {
    id: 'review',
    label: 'Review & Correct',
    icon: <Eye className="w-5 h-5" />,
    title: 'Review & Correct',
    description: 'Open any uploaded invoice to read it in plain language, fix anything that\'s off, and the system learns from every correction.',
    example: 'The PO number came out wrong. Open the invoice, read it on the Details tab, click the field, type the right value, confirm. Next time ViewPoint sees a Digital AIM invoice, it gets the PO right — automatically.',
    tips: [
      'The Details tab reads the invoice back in plain, everyday terms',
      'Each correction teaches the system',
      'Add missing fields with "+ Add Entity"',
      'Stuck? Hit Ask AI on the invoice for an instant answer',
      'Set invoice direction (incoming/outgoing) and track status workflow',
    ],
    cta: 'Go to Uploaded Invoices',
    route: '/uploaded-invoices',
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: <Layout className="w-5 h-5" />,
    title: 'Templates',
    description: 'Upload one invoice from a supplier you get bills from often, and future invoices from them are read more accurately.',
    example: 'Digital AIM sends invoices every month with the same layout. Add one of their invoices as a template, and future Digital AIM invoices are read more accurately automatically.',
    tips: [
      'Best for suppliers who bill you regularly',
      'Just upload one sample invoice — no setup needed',
      'We handle the rest when matching invoices arrive',
      'Your plan includes a set number of templates — higher plans allow more',
    ],
    cta: 'Go to Templates',
    route: '/uploaded-invoices/templates',
  },
  {
    id: 'create',
    label: 'Create Invoices',
    icon: <Receipt className="w-5 h-5" />,
    title: 'Create Invoices',
    description: 'Generate professional invoices from scratch and track them from draft to paid.',
    example: 'Need to bill a client? Fill in the form, pick your client, add line items. Download a ready-to-send PDF in 2 minutes.',
    tips: [
      'Standard, VAT, Proforma, Credit Note types',
      'Set direction: outgoing (AR) or incoming (AP)',
      'Track workflow: Ready to Process → Confirmed → Sent → Paid',
      'Mark invoices overdue automatically when past due date',
    ],
    cta: 'Go to Created Invoices',
    route: '/created-invoices',
  },
  {
    id: 'expenses',
    label: 'Expenses',
    icon: <Coins className="w-5 h-5" />,
    title: 'Expenses',
    description: 'Track the money going out — log what you spend and see it grouped by category, in the currency you choose.',
    example: 'You pay €120 for office supplies and a €40 software subscription. Log them under Expenses and your This month, Unpaid, Paid and Overdue totals update instantly — with a clear by-category breakdown.',
    tips: [
      'See This month, Unpaid, Paid and Overdue at a glance',
      'Break spending down by category',
      'Each expense keeps its original currency; totals convert automatically',
      'Mark bills paid and catch overdue ones early',
      'Or just tell the AI Assistant: "record an expense of 240 EUR for software"',
    ],
    cta: 'Go to Expenses',
    route: '/expenses',
  },
  {
    id: 'ai',
    label: 'AI Assistant',
    icon: <Brain className="w-5 h-5" />,
    title: 'AI Assistant',
    description: 'Ask anything about your documents and expenses in plain English — no filters, no reports, just a question. You can also record an expense by describing it.',
    example: 'Say "record an expense of 240 EUR for software, February" and the assistant fills in a card for you to check. Adjust anything, pick the exact date, then save — nothing is written until you press Add.',
    tips: [
      'Find overdue invoices, filter by vendor, calculate totals',
      'Record expenses by describing them; you always confirm before anything is saved',
      'Ask what you spent — "how much did I spend on software in February?"',
      'Ask one question at a time — each is answered on its own, without the previous replies',
      'Jump in from Ask AI buttons across invoices, fields and expenses',
      'Each question uses 1 AI query (check your plan limit)',
    ],
    cta: 'Go to AI Assistant',
    route: '/ai',
  },
  {
    id: 'ksef',
    label: 'KSeF e-invoicing',
    icon: <Landmark className="w-5 h-5" />,
    title: 'KSeF e-invoicing (Poland)',
    description: 'Submit invoices directly to Poland\'s National e-Invoice System (KSeF) without leaving ViewPoint — generated, signed, encrypted, and delivered to the Ministry of Finance.',
    example: 'You issue an invoice from a Polish seller (NIP attached). Click "Submit to KSeF" — ViewPoint builds the FA(3) XML, authenticates, submits, polls until accepted, and stores the official UPO receipt (XML) for your records.',
    tips: [
      'Available on the Advanced plan only',
      'Choose your environment: Test (sandbox), Demo (pre-prod), Production (legally binding)',
      'Authenticate with a KSeF token, upload a .p12 certificate, or enroll a qualified signature',
      'Once KSeF assigns a number the invoice cannot be deleted — corrections require a credit/corrective invoice',
      'Proforma invoices are not submittable (by design)',
      'Track status per invoice with the KSeF chip: Not Submitted, Processing, Accepted, Rejected',
    ],
    cta: 'Open Invoice Settings',
    route: '/created-invoices/settings',
    badge: 'Advanced',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <SettingsIcon className="w-5 h-5" />,
    title: 'Invoice Settings & Sellers',
    description: 'Set up your seller profiles, invoice numbering, default currency, language, and logo — used everywhere ViewPoint creates an invoice.',
    example: 'Add a seller profile with NIP, IBAN, and logo. Set the prefix "INV-" and 5-digit padding. Every new invoice automatically uses INV-00042, your bank details, and your branding.',
    tips: [
      'Multiple seller profiles (for multi-entity businesses) — mark one as default',
      'Per-seller logo upload + bank/IBAN details',
      'Configurable invoice number prefix, padding, and language (EN/PL/RO)',
      'KSeF connection is configured per seller from this page (Advanced plan)',
    ],
    cta: 'Open Invoice Settings',
    route: '/created-invoices/settings',
  },
];

// Note: this page no longer writes the legacy 'hasSeenGuide' flag. Where a login
// lands is now decided by a per-user login counter (see AuthContext), which the
// old flag would skew — opening the Guide from the nav once used to switch off
// the new-user landing for everyone on the browser, including later accounts.

const UserGuidePage = () => {
  const navigate = useNavigate();
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="guide-root">
      <div className="guide-inner">

        {/* Header */}
        <div className="guide-header">
          <div className="guide-header-row">
            <div className="guide-header-chip">
              <BookOpen />
            </div>
            <div>
              <h1 className="guide-title">ViewPoint Guide</h1>
              <p className="guide-subtitle">Everything you need, in one place.</p>
            </div>
          </div>
        </div>

        {/* Anchor Nav */}
        <div className="guide-nav">
          {SECTIONS.map((s, idx) => (
            <button
              key={s.id}
              data-i={idx}
              className="guide-pill"
              onClick={() => scrollTo(s.id)}
            >
              {s.icon}
              {s.label}
            </button>
          ))}
        </div>

        {/* Feature Sections */}
        <div className="guide-cards">
          {SECTIONS.map((s, idx) => (
            <div
              key={s.id}
              data-i={idx}
              ref={el => { sectionRefs.current[s.id] = el; }}
              className="guide-card"
            >
              <div className="guide-card-body">

                {/* Left: icon + title + description */}
                <div className="guide-card-main">
                  <div className="guide-card-title-row">
                    <span className="guide-icon-chip">{s.icon}</span>
                    <h2 className="guide-card-title">{s.title}</h2>
                    {s.badge && (
                      <span className="guide-badge">
                        <Crown /> {s.badge}
                      </span>
                    )}
                  </div>
                  <p className="guide-desc">{s.description}</p>

                  {/* Example */}
                  <div className="guide-example">
                    <Sparkles />
                    <p>{s.example}</p>
                  </div>

                  {/* Tips */}
                  <ul className="guide-tips">
                    {s.tips.map((tip, i) => (
                      <li key={i} className="guide-tip">
                        <CheckCircle />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Right: CTA button */}
                <div className="guide-card-side">
                  <button className="guide-cta-btn" onClick={() => navigate(s.route)}>
                    {s.cta}
                    <ArrowRight />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="guide-footer">
          <div className="guide-footer-title-row">
            <Target />
            <h3>Ready? Let's go.</h3>
          </div>
          <p>Upload or create your first invoice.</p>
          <div className="guide-footer-actions">
            <button className="guide-cta-btn" onClick={() => navigate('/dashboard')}>
              <Upload />
              Upload Invoice
            </button>
            <button className="guide-cta-btn" onClick={() => navigate('/created-invoices/new')}>
              <FileText />
              Create Invoice
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserGuidePage;
